import { FunctionComponent, useState } from "react";
import { Button } from "../../components/BaseComponents";
import { createCurrencyFormatter, centsToCurrency } from "../../lib/utils";
import { CurrencyCode } from "../../types/currency";
import { SortDirection } from "../../types/SortDirection";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";

export type PcafSummaryCategory = {
	instrumentCategory: string,
	sumOutstandingAmount: number,
	sumWeightedOutstandingAmount: number,
	dataQualityScore: number,
};

export type PcafSummaryReport = {
	currencyCode: CurrencyCode,
	categories: PcafSummaryCategory[],
	grandTotal: {
		sumOutstandingAmount: number,
		sumWeightedOutstandingAmount: number,
		dataQualityScore: number | null,
	},
	dataQualityScale: Array<{ score: number, title: string, description: string }>,
};

type PortfolioPcafSummaryTableProps = {
	report: PcafSummaryReport | null,
	isLoading?: boolean,
};

const GRAND_TOTAL_ID = 'grand-total';
const DEFAULT_SORT_KEY = 'instrumentCategory';
const DEFAULT_SORT_DIRECTION: SortDirection = 'ascending';

const formatScore = (score: number | null): string => {
	if (score === null) {
		return '-';
	}
	return score.toFixed(1);
};

const PortfolioPcafSummaryTable: FunctionComponent<PortfolioPcafSummaryTableProps> = ({ report, isLoading }) => {
	const [sortKey, setSortKey] = useState(DEFAULT_SORT_KEY);
	const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_SORT_DIRECTION);
	const [showLegend, setShowLegend] = useState(false);

	if (isLoading || !report) {
		return null;
	}

	if (report.categories.length === 0) {
		return null;
	}

	const formatCurrency = createCurrencyFormatter(report.currencyCode).format;

	const columns: TableColumn[] = [
		{ id: 'instrumentCategory', label: 'PCAF Category', sortable: true, textAlign: 'left' },
		{
			id: 'sumOutstandingAmount',
			label: 'Outstanding Capital Amount',
			sortable: true,
			textAlign: 'right',
			renderer: (key, row) => <span>{formatCurrency(centsToCurrency(row[key]) ?? 0)}</span>,
		},
		{
			id: 'sumWeightedOutstandingAmount',
			label: 'Weighted Outstanding Capital Amount',
			sortable: true,
			textAlign: 'right',
			renderer: (key, row) => <span>{formatCurrency(centsToCurrency(row[key]) ?? 0)}</span>,
		},
		{
			id: 'dataQualityScore',
			label: 'Data Quality Score',
			sortable: true,
			textAlign: 'right',
			renderer: (key, row) => <span>{formatScore(row[key])}</span>,
		},
	];

	const direction = sortDirection === 'ascending' ? 1 : -1;
	const sortedCategories = [...report.categories].sort((a, b) => {
		const aValue = a[sortKey as keyof PcafSummaryCategory];
		const bValue = b[sortKey as keyof PcafSummaryCategory];
		if (typeof aValue === 'string' || typeof bValue === 'string') {
			return String(aValue).localeCompare(String(bValue)) * direction;
		}
		return ((aValue as number) - (bValue as number)) * direction;
	});

	// Grand total is a pinned summary row, always shown last regardless of the active sort.
	const rows = [
		...sortedCategories.map(category => ({ _id: category.instrumentCategory, ...category })),
		{
			_id: GRAND_TOTAL_ID,
			instrumentCategory: 'Grand Total',
			sumOutstandingAmount: report.grandTotal.sumOutstandingAmount,
			sumWeightedOutstandingAmount: report.grandTotal.sumWeightedOutstandingAmount,
			dataQualityScore: report.grandTotal.dataQualityScore,
		},
	];

	return (
		<div className="mt-8" data-testid="portfolio-pcaf-summary-table">
			<h3 className="text-lg text-charcoal-100 mb-2">PCAF Portfolio Summary Report</h3>
			<SimpleSortTable
				data={rows}
				columns={columns}
				defaultSorting={{ key: DEFAULT_SORT_KEY, direction: DEFAULT_SORT_DIRECTION }}
				onSortPress={(key, newDirection) => {
					setSortKey(key);
					setSortDirection(newDirection as SortDirection);
				}}
				rowClassName={row => row._id === GRAND_TOTAL_ID
					? 'bg-charcoal-800 text-charcoal-050'
					: 'bg-charcoal-700 text-white'}
			/>
			<div className="rounded-lg bg-sun-500 p-4 mt-4">
				<div className={`rounded-lg bg-white p-4 mb-4 text-charcoal-800 text-sm ${!showLegend && 'hidden'}`} id="dataQualityLegendSection" data-testid="data-quality-legend-section">
					<p className="mb-2">Data Quality Score legend</p>
					<table>
						<tbody>
							{report.dataQualityScale.map(scale => (
								<tr key={scale.score}>
									<td className="align-top pr-4 py-0.5 whitespace-nowrap">
										{scale.score}. {scale.title}
									</td>
									<td className="align-top py-0.5">{scale.description}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<Button
					type="Primary"
					text={showLegend ? 'Hide Data Quality Legend' : 'Show Data Quality Legend'}
					hasLeadingIcon
					size="sm"
					icon={`fa-solid ${showLegend ? 'fa-minus' : 'fa-plus'}`}
					onClick={() => setShowLegend(!showLegend)}
					ariaControls="dataQualityLegendSection"
					ariaExpanded={showLegend}
				/>
			</div>
		</div>
	);
};

export default PortfolioPcafSummaryTable;
